import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui';
import { LineCard, DualLineCard, type Point, type DualPoint } from '../components/Chart';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface DailyRow { date?: string; pageviews?: number; attempt?: number; clicks?: number; cost?: number }
interface Today { pageviews?: number; attempt?: number; clicks?: number; cost?: number }
interface ClientRow { clientName: string; activeCampaigns: number; totalCost: number }
interface DashData {
  totalClicks?: number; totalCost?: number; activeCampaigns?: number;
  dailyData?: DailyRow[]; todayData?: Today; today?: Today; clientsData?: ClientRow[] | null;
}

const DATE_FILTERS = [
  { v: 'today', l: 'Today' }, { v: 'yesterday', l: 'Yesterday' }, { v: 'last7days', l: 'Last 7 days' },
  { v: 'last30days', l: 'Last 30 days' }, { v: 'currentMonth', l: 'Current Month' },
  { v: 'lastMonth', l: 'Last Month' }, { v: 'last60days', l: 'Last 60 days' },
];
import { calendarShort } from '../lib/calendarDate';
const n = (v: unknown) => Number(v) || 0;

// Data de calendário (coluna DATE): não pode passar por Date, senão o
// navegador em BRT renderiza o dia anterior.
function dayLabel(s?: string): string {
  return s ? calendarShort(s) : '';
}

function Tile({ value, label, sub, tone }: { value: string; label: string; sub?: string; tone: string }) {
  return (
    <div className={`rounded-xl p-5 text-white ${tone}`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-sm font-medium mt-1">{label}</div>
      {sub && <div className="text-xs opacity-90">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState<DashData | null>(null);
  const [dateFilter, setDateFilter] = useState('last30days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get<ApiResponse<DashData>>(`/dashboard/data?dateFilter=${dateFilter}`)
      .then((r) => { setData(r.data || null); setError(null); })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [dateFilter]);

  const daily = data?.dailyData || [];
  const today = data?.todayData || data?.today || {};
  const clients = data?.clientsData || [];
  const mk = (sel: (r: DailyRow) => number): Point[] => daily.map((r) => ({ x: dayLabel(r.date), y: sel(r) }));
  const clicksCost: DualPoint[] = daily.map((r) => ({ x: dayLabel(r.date), a: n(r.clicks), b: n(r.cost) }));
  const todayPct = n(today.pageviews) ? (n(today.attempt) / n(today.pageviews)) * 100 : 0;

  return (
    <Layout title="Dashboard">
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}

      {/* Date filter */}
      <Card className="p-4 mb-5">
        <div className="flex flex-wrap gap-2">
          {DATE_FILTERS.map((f) => (
            <button key={f.v} onClick={() => setDateFilter(f.v)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${dateFilter === f.v ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'}`}>{f.l}</button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">Loading…</Card>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Tile value={n(data?.totalClicks).toLocaleString()} label="Total Clicks" sub="Total clicks in selected period" tone="bg-rose-500" />
            <Tile value={n(data?.activeCampaigns).toLocaleString()} label="Active Campaigns" sub="Campaigns currently active" tone="bg-emerald-600" />
            <Tile value={`$${n(data?.totalCost).toFixed(2)}`} label="Total Cost" sub="Total cost in selected period (USD)" tone="bg-sky-600" />
          </div>

          {/* Daily Clicks and Cost */}
          <div className="mb-6">
            <DualLineCard title="Daily Clicks and Cost" data={clicksCost} aName="Clicks" bName="Cost" aColor="var(--series-ink)" bColor="#14b8a6" />
          </div>

          {/* Today tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Tile value={n(today.pageviews).toLocaleString()} label="Pageviews (Today)" sub="Total pageviews today" tone="bg-violet-500" />
            <Tile value={n(today.attempt).toLocaleString()} label="Attempt (Today)" sub="Total attempts today" tone="bg-rose-500" />
            <Tile value={`${todayPct.toFixed(2)}%`} label="% Attempt (Today)" sub="Percent attempt today" tone="bg-amber-500" />
            <Tile value={n(today.clicks).toLocaleString()} label="Clicks (Today)" sub="Total clicks today" tone="bg-emerald-600" />
            <Tile value={`$${n(today.cost).toFixed(2)}`} label="Cost (Today)" sub="Total cost today" tone="bg-sky-600" />
          </div>

          {/* Daily charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
            <LineCard title="Pageviews per Day" data={mk((r) => n(r.pageviews))} color="#8b5cf6" />
            <LineCard title="Attempt per Day" data={mk((r) => n(r.attempt))} color="#ef4444" />
            <LineCard title="% Attempt per Day" data={mk((r) => (n(r.pageviews) ? (n(r.attempt) / n(r.pageviews)) * 100 : 0))} color="#8b5cf6" format={(v) => `${v.toFixed(2)}%`} />
            <LineCard title="Clicks per Day" data={mk((r) => n(r.clicks))} color="#22c55e" />
            <LineCard title="Cost per Day" data={mk((r) => n(r.cost))} color="#f59e0b" format={(v) => `$${v.toFixed(0)}`} />
          </div>

          {/* Data by Client */}
          {clients.length > 0 && (
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">Data by Client</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                      <th className="px-4 py-3 font-medium">Client Name</th>
                      <th className="px-4 py-3 font-medium text-right">Active Campaigns</th>
                      <th className="px-4 py-3 font-medium text-right">Total Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {clients.map((c, i) => (
                      <tr key={i} className="hover:bg-surface-muted/60">
                        <td className="px-4 py-3 font-medium text-ink-900">{c.clientName}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{n(c.activeCampaigns)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">${n(c.totalCost).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </Layout>
  );
}
